import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Youtube, 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Calendar, 
  BarChart3, 
  Sparkles,
  Eye,
  RefreshCw,
  Settings,
  Share2,
  Heart,
  MessageCircle,
  Clock
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// TypeScript interfaces for API responses
interface Channel {
  id: number;
  name: string;
  platform: string;
  accountHandle: string;
  accountId?: string;
  accessToken?: string;
  profileImage?: string;
  followerCount?: number;
  needsReauth?: boolean;
  postCount?: number;
  createdAt: string;
}

interface Post {
  id: number;
  title: string;
  content: string;
  platforms: string[];
  status: string;
  postedAt?: string;
  scheduledAt?: string;
  createdAt: string;
  metadata?: any;
}

interface PlatformStatus {
  connected: boolean;
  channels?: Channel[];
  accountInfo?: {
    name: string;
    profileImage?: string;
    followerCount?: number;
  };
}

interface Analytics {
  id: number;
  platform: string;
  metric: string;
  value: number;
  date: string;
}

interface Template {
  id: number;
  name: string;
  content: string;
  platforms: string[];
  category?: string;
}

interface CalendarDay {
  id: string;
  date: string;
  posts?: {
    id: number;
    time: string;
    title: string;
    platforms: string[];
  }[];
}

interface OAuthResponse {
  success: boolean;
  authUrl?: string;
  message?: string;
  error?: string;
}

// Form schemas
const channelFormSchema = z.object({
  name: z.string().min(1, "Channel name is required"),
  platform: z.string().min(1, "Platform is required"),
  accountHandle: z.string().min(1, "Account handle is required"),
  accountId: z.string().optional(),
  accessToken: z.string().optional(),
});

const postFormSchema = z.object({
  title: z.string().min(1, "Post title is required"),
  content: z.string().min(1, "Content is required"),
  platforms: z.array(z.string()).min(1, "At least one platform must be selected"),
  status: z.string().default("draft"),
  scheduledAt: z.string().optional(),
});

const whatsappCredentialsSchema = z.object({
  accessToken: z.string().min(1, "Access Token is required"),
  phoneNumberId: z.string().min(1, "Phone Number ID is required"),
  businessAccountId: z.string().optional(),
  webhookToken: z.string().optional(),
});

const facebookPageCredentialsSchema = z.object({
  pageAccessToken: z.string().min(1, "Page Access Token is required"),
  pageId: z.string().min(1, "Page ID is required"),
  pageName: z.string().min(1, "Page Name is required"),
});

type ChannelFormData = z.infer<typeof channelFormSchema>;
type PostFormData = z.infer<typeof postFormSchema>;
type WhatsAppCredentialsData = z.infer<typeof whatsappCredentialsSchema>;
type FacebookPageCredentialsData = z.infer<typeof facebookPageCredentialsSchema>;

export default function SocialMediaManagement() {
  const { toast } = useToast();
  const [activeSubTab, setActiveSubTab] = useState("channels");
  const [isChannelDialogOpen, setIsChannelDialogOpen] = useState(false);
  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [platformStatus, setPlatformStatus] = useState<Record<string, any>>({});
  const [isConnecting, setIsConnecting] = useState<Record<string, boolean>>({});
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);
  const [isFacebookPageDialogOpen, setIsFacebookPageDialogOpen] = useState(false);
  const [selectedFacebookChannelId, setSelectedFacebookChannelId] = useState<string>("");
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<any>(null);
  const [isPostAnalyticsDialogOpen, setIsPostAnalyticsDialogOpen] = useState(false);
  const [analyticsPost, setAnalyticsPost] = useState<any>(null);

  // Data queries
  const { data: channels = [], isLoading: channelsLoading } = useQuery<Channel[]>({
    queryKey: ['/api/admin/social/channels'],
  });

  const { data: posts = [], isLoading: postsLoading } = useQuery<Post[]>({
    queryKey: ['/api/admin/social/posts'],
  });

  const { data: analytics = [], isLoading: analyticsLoading } = useQuery<Analytics[]>({
    queryKey: ['/api/admin/social/analytics'],
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery<Template[]>({
    queryKey: ['/api/admin/social/templates'],
  });

  const { data: calendar = [], isLoading: calendarLoading } = useQuery<CalendarDay[]>({
    queryKey: ['/api/admin/social/calendar'],
  });

  // Platform connection status queries
  const { data: facebookStatus, isLoading: facebookLoading } = useQuery<PlatformStatus>({
    queryKey: ['/api/admin/social/status/facebook'],
  });

  const { data: youtubeStatus, isLoading: youtubeLoading } = useQuery<PlatformStatus>({
    queryKey: ['/api/admin/social/status/youtube'],
  });

  const { data: tiktokStatus, isLoading: tiktokLoading } = useQuery<PlatformStatus>({
    queryKey: ['/api/admin/social/status/tiktok'],
  });

  const { data: whatsappStatus, isLoading: whatsappLoading } = useQuery<PlatformStatus>({
    queryKey: ['/api/admin/social/whatsapp/status'],
  });

  // Mutations
  const createChannelMutation = useMutation({
    mutationFn: async (data: ChannelFormData) => {
      const response = await apiRequest('POST', '/api/admin/social/channels', data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
      setIsChannelDialogOpen(false);
      channelForm.reset();
      toast({ title: "Success", description: "Social channel created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create social channel", variant: "destructive" });
    },
  });

  const createPostMutation = useMutation({
    mutationFn: async (data: PostFormData) => {
      const response = await apiRequest('POST', '/api/admin/social/posts', data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      setIsPostDialogOpen(false);
      postForm.reset();
      toast({ title: "Success", description: "Social post created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create social post", variant: "destructive" });
    },
  });

  const generatePostMutation = useMutation({
    mutationFn: async (data: { prompt: string; platforms: string[] }) => {
      const response = await apiRequest('POST', '/api/admin/social/generate-post', data);
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      setIsGenerating(false);
      setAiPrompt("");
      // If the API returned the created post, open it in the
      // Posts tab so the user can immediately review, edit, and publish.
      if (data && typeof data === 'object') {
        setActiveSubTab('posts');
        setSelectedPost(data as any);
        postForm.setValue('title', data.title || '');
        postForm.setValue('content', data.content || '');
        postForm.setValue('platforms', data.platforms || []);
        postForm.setValue('status', data.status || 'draft');
        setIsPostDialogOpen(true);
        toast({ title: "Success", description: "AI post generated. You can now edit and publish it." });
        return;
      }

      toast({ title: "Success", description: "AI post generated successfully" });
    },
    onError: (error: any) => {
      setIsGenerating(false);
      const message = error.message?.includes('OpenAI API key') 
        ? "OpenAI API key not configured. Please add your OpenAI API key to generate AI posts."
        : "Failed to generate AI post";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const scheduleOptimalMutation = useMutation({
    mutationFn: async (data: { postId: number; channelId?: number; platforms: string[] }) => {
      const response = await apiRequest('POST', '/api/admin/social/schedule-optimal', data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/calendar'] });
      toast({ title: "Success", description: "Post scheduled for optimal time" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to schedule post optimally", variant: "destructive" });
    },
  });

  const adaptContentMutation = useMutation({
    mutationFn: async (data: { postId: number; content?: string; platforms: string[] }) => {
      const response = await apiRequest('POST', '/api/admin/social/adapt-content', data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Content adapted for platforms" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to adapt content", variant: "destructive" });
    },
  });

  const syncFacebookPostsMutation = useMutation({
    mutationFn: async (data: { channelId?: number; limit?: number }) => {
      const response = await apiRequest(
        "POST",
        "/api/admin/social/facebook/sync-posts",
        data
      );
      return await response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
      toast({
        title: "Success",
        description: result?.message || "Facebook posts synced successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to sync Facebook posts",
        variant: "destructive",
      });
    },
  });

  const syncEngagementMutation = useMutation({
    mutationFn: async (postId: number) => {
      const response = await apiRequest('POST', `/api/admin/social/posts/${postId}/sync-engagement`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Engagement metrics updated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to sync engagement", variant: "destructive" });
    },
  });

  const editPostMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest('PUT', `/api/admin/social/posts/${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Post updated successfully" });
      setIsPostDialogOpen(false);
      setSelectedPost(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update post", variant: "destructive" });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: async (postId: number) => {
      const response = await apiRequest('DELETE', `/api/admin/social/posts/${postId}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Post deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete post", variant: "destructive" });
    },
  });

  const publishPostMutation = useMutation({
    mutationFn: async ({ postId, channelId }: { postId: number; channelId: number }) => {
      const response = await apiRequest('POST', `/api/admin/social/posts/${postId}/publish`, { channelId });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Post published to Facebook!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to publish post", variant: "destructive" });
    },
  });

  const schedulePostMutation = useMutation({
    mutationFn: async ({ postId, scheduledAt }: { postId: number; scheduledAt: string }) => {
      const response = await apiRequest('POST', `/api/admin/social/posts/${postId}/schedule`, { scheduledAt });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/posts'] });
      toast({ title: "Success", description: "Post scheduled successfully" });
      setIsScheduleDialogOpen(false);
      setScheduledTime("");
      setSelectedPost(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to schedule post", variant: "destructive" });
    },
  });

  // Disconnect platform mutation
  const disconnectPlatformMutation = useMutation({
    mutationFn: async ({ platform, channelId }: { platform: string; channelId: number }) => {
      const response = await apiRequest('POST', `/api/admin/social/disconnect/${platform}`, { channelId });
      return await response.json();
    },
    onSuccess: (_, { platform }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/social/status/${platform}`] });
      toast({ title: "Success", description: `${platform} account disconnected successfully` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect account", variant: "destructive" });
    },
  });

  // WhatsApp credentials mutation
  const whatsappCredentialsMutation = useMutation({
    mutationFn: async (data: WhatsAppCredentialsData) => {
      const response = await apiRequest('POST', '/api/admin/social/whatsapp/credentials', data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/whatsapp/status'] });
      setIsWhatsAppDialogOpen(false);
      whatsappForm.reset();
      toast({ title: "Success", description: "WhatsApp credentials saved successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to save WhatsApp credentials", 
        variant: "destructive" 
      });
    },
  });

  const facebookPageCredentialsMutation = useMutation({
    mutationFn: async (data: FacebookPageCredentialsData) => {
      const response = await apiRequest('POST', '/api/admin/social/facebook/page', {
        body: data,
      });
      return await response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/social/status/facebook'] });
      setIsFacebookPageDialogOpen(false);
      facebookPageForm.reset();
      toast({
        title: "Success",
        description: result?.message || "Facebook page connected successfully. You can now sync posts!",
      });
    },
    onError: (error: any) => {
      console.error('Facebook page connect error:', error);
      
      // Extract detailed error information from the response
      const errorMessage = error.message || "Failed to connect Facebook page";
      const details = error.details;
      const facebookError = error.facebookError;
      
      // Create a comprehensive error message
      let description = errorMessage;
      if (details) {
        description = details;
      }
      if (facebookError) {
        description += `\n\nFacebook Error: ${facebookError}`;
      }
      
      toast({
        title: "Facebook Connection Failed",
        description: description,
        variant: "destructive",
      });
    },
  });

  // Form instances
  const channelForm = useForm<ChannelFormData>({
    resolver: zodResolver(channelFormSchema),
    defaultValues: {
      name: "",
      platform: "",
      accountHandle: "",
      accountId: "",
      accessToken: "",
    },
  });

  const postForm = useForm<PostFormData>({
    resolver: zodResolver(postFormSchema),
    defaultValues: {
      title: "",
      content: "",
      platforms: [],
      status: "draft",
      scheduledAt: "",
    },
  });

  const facebookPageForm = useForm<FacebookPageCredentialsData>({
    resolver: zodResolver(facebookPageCredentialsSchema),
    defaultValues: {
      pageAccessToken: "",
      pageId: "",
      pageName: "",
    },
  });

  const whatsappForm = useForm<WhatsAppCredentialsData>({
    resolver: zodResolver(whatsappCredentialsSchema),
    defaultValues: {
      accessToken: "",
      phoneNumberId: "",
      businessAccountId: "",
      webhookToken: "",
    },
  });

  const handleCreateChannel = (data: ChannelFormData) => {
    createChannelMutation.mutate(data);
  };

  const handleCreatePost = (data: PostFormData) => {
    const submitData = {
      ...data,
      scheduledAt: data.scheduledAt || null,
    };
    createPostMutation.mutate(submitData);
  };

  const handleGenerateAIPost = () => {
    if (!aiPrompt.trim()) {
      toast({ title: "Error", description: "Please enter a prompt for AI generation", variant: "destructive" });
      return;
    }

    setIsGenerating(true);
    generatePostMutation.mutate({
      prompt: aiPrompt,
      platforms: ['facebook', 'twitter', 'instagram'],
    });
  };

  const handleWhatsAppCredentials = (data: WhatsAppCredentialsData) => {
    whatsappCredentialsMutation.mutate(data);
  };

  const handleFacebookPageCredentials = (data: FacebookPageCredentialsData) => {
    facebookPageCredentialsMutation.mutate(data);
  };

  const handleSyncFacebookPosts = () => {
    let channelId: number | undefined;

    if (selectedFacebookChannelId) {
      const parsed = Number(selectedFacebookChannelId);
      if (!Number.isNaN(parsed)) {
        channelId = parsed;
      }
    } else if (facebookStatus?.channels && facebookStatus.channels.length > 0) {
      channelId = (facebookStatus.channels[0] as any).id;
    }

    syncFacebookPostsMutation.mutate(
      channelId ? { channelId } : {}
    );
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return <Facebook className="h-4 w-4" />;
      case 'twitter': return <Twitter className="h-4 w-4" />;
      case 'instagram': return <Instagram className="h-4 w-4" />;
      case 'linkedin': return <Linkedin className="h-4 w-4" />;
      case 'youtube': return <Youtube className="h-4 w-4" />;
      case 'tiktok': return <Share2 className="h-4 w-4" />; // TikTok icon placeholder
      case 'whatsapp': return <span className="h-4 w-4 flex items-center justify-center text-green-600 font-bold">💬</span>; // WhatsApp icon
      default: return <Share2 className="h-4 w-4" />;
    }
  };

  // OAuth connection functions
  const handleOAuthConnect = async (platform: string) => {
    setIsConnecting(prev => ({ ...prev, [platform]: true }));
    
    try {
      const response = await apiRequest('GET', `/api/admin/social/connect/${platform}`);
      const oauthData: OAuthResponse = await response.json();
      
      if (oauthData.authUrl) {
        // Open OAuth popup
        const popup = window.open(
          oauthData.authUrl,
          `${platform}_oauth`,
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Listen for postMessage from OAuth popup
        const handleMessage = (event: MessageEvent) => {
          // For security, we should validate the origin, but since we control the popup URL, we'll accept all for now
          if (event.data?.type === 'oauth_success' && event.data?.platform === platform) {
            // OAuth success
            popup?.close();
            setIsConnecting(prev => ({ ...prev, [platform]: false }));
            
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['/api/admin/social/channels'] });
            queryClient.invalidateQueries({ queryKey: [`/api/admin/social/status/${platform}`] });
            
            toast({ 
              title: "Success", 
              description: event.data.message || `${platform} account connected successfully` 
            });
            
            // Remove event listener
            window.removeEventListener('message', handleMessage);
          } else if (event.data?.type === 'oauth_error' && event.data?.platform === platform) {
            // OAuth error
            popup?.close();
            setIsConnecting(prev => ({ ...prev, [platform]: false }));
            
            toast({ 
              title: "Error", 
              description: event.data.error || `Failed to connect ${platform}`, 
              variant: "destructive" 
            });
            
            // Remove event listener
            window.removeEventListener('message', handleMessage);
          }
        };

        // Add message listener
        window.addEventListener('message', handleMessage);

        // Fallback: Check if popup is closed manually (in case postMessage fails)
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setIsConnecting(prev => ({ ...prev, [platform]: false }));
            window.removeEventListener('message', handleMessage);
            
            // Only show error if we haven't received a success message
            if (isConnecting[platform]) {
              toast({ 
                title: "Warning", 
                description: `${platform} connection window closed. Please try again if the connection was not successful.`,
                variant: "destructive" 
              });
            }
          }
        }, 1000);

        // Cleanup function to remove listeners if component unmounts
        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosed);
        }, 300000); // 5 minutes timeout
      }
    } catch (error: any) {
      setIsConnecting(prev => ({ ...prev, [platform]: false }));
      toast({ 
        title: "Error", 
        description: error.message || `Failed to initiate ${platform} connection`, 
        variant: "destructive" 
      });
    }
  };

  const handleDisconnect = (platform: string, channelId: number) => {
    disconnectPlatformMutation.mutate({ platform, channelId });
  };

  const getPlatformStatus = (platform: string) => {
    switch (platform) {
      case 'facebook': return facebookStatus;
      case 'youtube': return youtubeStatus;
      case 'tiktok': return tiktokStatus;
      case 'whatsapp': return whatsappStatus;
      default: return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: any = {
      draft: "secondary",
      scheduled: "outline",
      posted: "default",
      failed: "destructive"
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Social Media Management
          </CardTitle>
          <CardDescription>
            Manage your social channels, create posts, and track analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="channels">Channels</TabsTrigger>
              <TabsTrigger value="posts">Posts</TabsTrigger>
              <TabsTrigger value="scheduling">Scheduling</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="ai-generator">AI Generator</TabsTrigger>
            </TabsList>

            {/* Channels Tab */}
            <TabsContent value="channels" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Social Media Connections</h3>
              </div>
              
              {/* Platform Connection Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {['facebook', 'youtube', 'tiktok', 'whatsapp'].map((platform) => {
                  const status = getPlatformStatus(platform);
                  const isLoading = (platform === 'facebook' && facebookLoading) || 
                                  (platform === 'youtube' && youtubeLoading) || 
                                  (platform === 'tiktok' && tiktokLoading) ||
                                  (platform === 'whatsapp' && whatsappLoading);
                  const connecting = isConnecting[platform];
                  
                  return (
                    <Card key={platform} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {getPlatformIcon(platform)}
                            <h4 className="font-semibold capitalize">{platform}</h4>
                          </div>
                          {status?.connected ? (
                            <Badge variant="default" className="bg-green-500">
                              Connected
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              Not Connected
                            </Badge>
                          )}
                        </div>
                        
                        {status?.connected ? (
                          <div className="space-y-3">
                            {status.channels?.map((channel: any) => (
                              <div key={channel.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                                <div className="flex items-center gap-2">
                                  {channel.profileImage && (
                                    <img 
                                      src={channel.profileImage} 
                                      alt={channel.name}
                                      className="w-8 h-8 rounded-full"
                                    />
                                  )}
                                  <div>
                                    <p className="font-medium text-sm" data-testid={`text-channel-${channel.id}`}>
                                      {channel.accountHandle}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {channel.followerCount ? `${channel.followerCount} followers` : ''}
                                    </p>
                                    {channel.needsReauth && (
                                      <p className="text-xs text-amber-600">⚠️ Needs re-authentication</p>
                                    )}
                                  </div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDisconnect(platform, channel.id)}
                                  disabled={disconnectPlatformMutation.isPending}
                                  data-testid={`button-disconnect-${channel.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleOAuthConnect(platform)}
                              disabled={connecting || isLoading}
                              className="w-full"
                              data-testid={`button-connect-additional-${platform}`}
                            >
                              {connecting ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Plus className="h-4 w-4 mr-2" />
                              )}
                              Add Another Account
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Connect your {platform} account to manage posts and view analytics.
                            </p>
                            <Button 
                              onClick={() =>
                                platform === "whatsapp"
                                  ? setIsWhatsAppDialogOpen(true)
                                  : platform === "facebook"
                                  ? setIsFacebookPageDialogOpen(true)
                                  : handleOAuthConnect(platform)
                              }
                              disabled={connecting || isLoading}
                              className="w-full primary-gradient"
                              data-testid={`button-connect-${platform}`}
                            >
                              {connecting ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                getPlatformIcon(platform)
                              )}
                              <span className="ml-2">
                                {connecting ? 'Connecting...' : `Connect ${platform.charAt(0).toUpperCase() + platform.slice(1)}`}
                              </span>
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Connected Channels List */}
              {channels && channels.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-md font-semibold">All Connected Channels</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Platform</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Posts</TableHead>
                        <TableHead>Connected</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {channels.map((channel: any) => (
                        <TableRow key={channel.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getPlatformIcon(channel.platform)}
                              <span className="capitalize">{channel.platform}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {channel.profileImage && (
                                <img 
                                  src={channel.profileImage} 
                                  alt={channel.accountHandle}
                                  className="w-6 h-6 rounded-full"
                                />
                              )}
                              <span>{channel.accountHandle}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {channel.needsReauth ? (
                              <Badge variant="destructive">Needs Auth</Badge>
                            ) : (
                              <Badge variant="default" className="bg-green-500">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell>{channel.postCount || 0}</TableCell>
                          <TableCell>
                            {new Date(channel.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDisconnect(channel.platform, channel.id)}
                              disabled={disconnectPlatformMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Scheduling Tab */}
            <TabsContent value="scheduling" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Content Calendar */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Content Calendar
                    </CardTitle>
                    <CardDescription>
                      Drag and drop scheduling with optimal timing
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Today's Schedule</h4>
                        <Badge variant="outline">{new Date().toLocaleDateString()}</Badge>
                      </div>
                      
                      <div className="space-y-2">
                        {calendar.length > 0 ? calendar.map((day: any) => (
                          <div key={day.id} className="border rounded-lg p-3">
                            <div className="font-medium mb-2">{day.date}</div>
                            {day.posts?.map((post: any) => (
                              <div key={post.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="text-sm">{post.time}</span>
                                  <span className="text-sm font-medium">{post.title}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {post.platforms.map((platform: string) => (
                                    <div key={platform} className="w-4 h-4">
                                      {getPlatformIcon(platform)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )) : (
                          <div className="text-center py-8 text-gray-500">
                            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No posts scheduled yet.</p>
                            <p className="text-sm">Use optimal scheduling to get started.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Optimal Timing */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Optimal Posting Times
                    </CardTitle>
                    <CardDescription>
                      Best times to post based on audience engagement
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">Friday</div>
                          <div className="text-sm text-green-700">Best Day</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">4:00 PM</div>
                          <div className="text-sm text-blue-700">Best Time</div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <h4 className="font-medium">Weekly Engagement Scores</h4>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                          const scores = [85, 78, 88, 82, 95, 75, 90];
                          return (
                            <div key={day} className="flex items-center justify-between">
                              <span className="text-sm">{day}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full">
                                  <div 
                                    className="h-2 bg-blue-500 rounded-full"
                                    style={{ width: `${scores[index]}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium">{scores[index]}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Post Optimization Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Post Optimization</CardTitle>
                  <CardDescription>
                    Optimize your posts for better engagement
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Auto-Schedule
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">
                          Schedule posts at optimal times for maximum engagement
                        </p>
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            const draftPost = posts.find((p: any) => p.status === 'draft');
                            if (!draftPost) {
                              // No draft yet – open the create post dialog preconfigured
                              // for scheduling so the user can choose a future date.
                              postForm.reset({
                                title: "",
                                content: "",
                                platforms: [],
                                status: "scheduled",
                                scheduledAt: "",
                              });
                              setActiveSubTab("posts");
                              setIsPostDialogOpen(true);
                              toast({
                                title: "Create and Schedule Post",
                                description:
                                  "Create your next post and choose a future date to schedule it.",
                              });
                              return;
                            }

                            // Draft exists – open the Schedule dialog so the user
                            // can select an exact future date/time.
                            setSelectedPost(draftPost);
                            setScheduledTime("");
                            setIsScheduleDialogOpen(true);
                          }}
                          disabled={scheduleOptimalMutation.isPending}
                        >
                          {scheduleOptimalMutation.isPending ? 'Scheduling...' : 'Schedule Next Post'}
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          Content Adaptation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">
                          Adapt content for different social platforms
                        </p>
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            // Prefer adapting a draft (work-in-progress),
                            // but fall back to the most recently posted item
                            // so published content can be repurposed.
                            const draftPost = posts.find((p: any) => p.status === 'draft');
                            const targetPost = draftPost || posts.find((p: any) => p.status === 'posted');

                            if (!targetPost) {
                              toast({ 
                                title: "No Posts Available", 
                                description: "Create a post first, then you can adapt its content for other platforms.",
                                variant: "destructive"
                              });
                              return;
                            }

                            adaptContentMutation.mutate({
                              postId: targetPost.id,
                              platforms: targetPost.platforms
                            });
                          }}
                          disabled={adaptContentMutation.isPending}
                        >
                          {adaptContentMutation.isPending ? 'Adapting...' : 'Adapt Content'}
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Performance Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">
                          Analyze best performing content types
                        </p>
                        <Button 
                          size="sm" 
                          className="w-full" 
                          variant="outline"
                          onClick={() => {
                            setActiveSubTab('analytics');
                          }}
                        >
                          View Insights
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Posts Tab */}
            <TabsContent value="posts" className="space-y-4">
              <div className="flex justify-between items-center gap-2">
                <h3 className="text-lg font-semibold">Social Posts</h3>
                <div className="flex items-center gap-2">
                  {facebookStatus?.connected && (
                    <>
                      {facebookStatus.channels && facebookStatus.channels.length > 0 && (
                        <Select
                          value={selectedFacebookChannelId}
                          onValueChange={setSelectedFacebookChannelId}
                        >
                          <SelectTrigger
                            className="w-48"
                            data-testid="select-facebook-channel"
                          >
                            <SelectValue placeholder="All Facebook channels" />
                          </SelectTrigger>
                          <SelectContent>
                            {facebookStatus.channels.map((channel: any) => (
                              <SelectItem
                                key={channel.id}
                                value={String(channel.id)}
                              >
                                {channel.accountHandle ||
                                  channel.name ||
                                  `Channel ${channel.id}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSyncFacebookPosts}
                      disabled={syncFacebookPostsMutation.isPending}
                      data-testid="button-sync-facebook-posts"
                    >
                      {syncFacebookPostsMutation.isPending ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Sync Facebook Posts
                        </>
                      )}
                    </Button>
                    </>
                  )}
                  <Dialog open={isPostDialogOpen} onOpenChange={(open) => {
                    setIsPostDialogOpen(open);
                    if (!open) {
                      // Clear selected post and form when dialog closes
                      setSelectedPost(null);
                      postForm.reset();
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        className="primary-gradient"
                        onClick={() => {
                          // Ensure we're creating a new post, not editing
                          setSelectedPost(null);
                          postForm.reset();
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Post
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{selectedPost ? 'Edit' : 'Create'} Social Post</DialogTitle>
                        <DialogDescription>
                          {selectedPost ? 'Edit your' : 'Create a new'} post for your social media channels
                        </DialogDescription>
                      </DialogHeader>
                      <Form {...postForm}>
                        <form onSubmit={postForm.handleSubmit((data) => {
                          if (selectedPost) {
                            editPostMutation.mutate({ id: selectedPost.id, data });
                          } else {
                            handleCreatePost(data);
                          }
                        })} className="space-y-4">
                          <FormField
                            control={postForm.control}
                            name="title"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Post Title</FormLabel>
                                <FormControl>
                                  <Input 
                                    placeholder="Internal title for your post" 
                                    data-testid="input-post-title"
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={postForm.control}
                            name="content"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Content</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    placeholder="Write your post content..." 
                                    rows={4}
                                    data-testid="textarea-post-content"
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={postForm.control}
                            name="platforms"
                            render={() => (
                              <FormItem>
                                <FormLabel>Target Platforms</FormLabel>
                                <div className="grid grid-cols-3 gap-3 mt-2">
                                  {['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok'].map((platform) => (
                                    <FormField
                                      key={platform}
                                      control={postForm.control}
                                      name="platforms"
                                      render={({ field }) => {
                                        return (
                                          <FormItem
                                            key={platform}
                                            className="flex flex-row items-start space-x-3 space-y-0"
                                          >
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value?.includes(platform)}
                                                onCheckedChange={(checked) => {
                                                  return checked
                                                    ? field.onChange([...field.value, platform])
                                                    : field.onChange(
                                                        field.value?.filter(
                                                          (value) => value !== platform
                                                        )
                                                      )
                                                }}
                                                data-testid={`checkbox-platform-${platform}`}
                                              />
                                            </FormControl>
                                            <div className="flex items-center gap-1">
                                              {getPlatformIcon(platform)}
                                              <FormLabel className="text-sm capitalize font-normal">
                                                {platform}
                                              </FormLabel>
                                            </div>
                                          </FormItem>
                                        )
                                      }}
                                    />
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={postForm.control}
                              name="status"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Status</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger data-testid="select-post-status">
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="draft">Draft</SelectItem>
                                      <SelectItem value="scheduled">Scheduled</SelectItem>
                                      <SelectItem value="posted">Post Now</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={postForm.control}
                              name="scheduledAt"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Schedule Date (optional)</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="datetime-local" 
                                      data-testid="input-scheduled-at"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={() => {
                                setIsPostDialogOpen(false);
                                setSelectedPost(null);
                                postForm.reset();
                              }}
                              data-testid="button-cancel-post"
                            >
                              Cancel
                            </Button>
                            <Button 
                              type="submit" 
                              disabled={createPostMutation.isPending || editPostMutation.isPending}
                              data-testid="button-submit-post"
                            >
                              {selectedPost 
                                ? (editPostMutation.isPending ? 'Updating...' : 'Update Post')
                                : (createPostMutation.isPending ? 'Creating...' : 'Create Post')}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>

                  {/* Schedule Dialog */}
                  <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Schedule Post</DialogTitle>
                        <DialogDescription>
                          Choose when you want this post to be published
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="schedule-time">Scheduled Date & Time</Label>
                          <Input 
                            id="schedule-time"
                            type="datetime-local" 
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setIsScheduleDialogOpen(false);
                              setScheduledTime("");
                              setSelectedPost(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => {
                              if (selectedPost && scheduledTime) {
                                schedulePostMutation.mutate({
                                  postId: selectedPost.id,
                                  scheduledAt: scheduledTime
                                });
                              }
                            }}
                            disabled={!scheduledTime || schedulePostMutation.isPending}
                          >
                            {schedulePostMutation.isPending ? 'Scheduling...' : 'Schedule'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="space-y-4">
                {posts.map((post: any) => (
                  <Card key={post.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{post.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            {post.platforms?.map((platform: string) => (
                              <div key={platform} className="flex items-center gap-1">
                                {getPlatformIcon(platform)}
                                <span className="text-xs capitalize">{platform}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(post.metadata?.source === 'facebook_sync' || post.metadata?.facebook_post_id) && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Facebook className="h-3 w-3" />
                              <span className="text-[10px] uppercase tracking-wide">Imported</span>
                            </Badge>
                          )}
                          {post.generatedByAi && (
                            <Badge variant="outline" className="text-blue-900">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI Generated
                            </Badge>
                          )}
                          {getStatusBadge(post.status)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">{post.content}</p>
                      
                      {/* Show engagement metrics if available */}
                      {post.metadata?.engagement && (
                        <div className="flex gap-4 mb-3 pb-3 text-sm border-b">
                          <div className="flex items-center gap-1">
                            <Heart className="h-4 w-4 text-red-500" />
                            <span>{post.metadata.engagement.likes}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="h-4 w-4 text-blue-500" />
                            <span>{post.metadata.engagement.comments}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Share2 className="h-4 w-4 text-green-500" />
                            <span>{post.metadata.engagement.shares}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => syncEngagementMutation.mutate(post.id)}
                            disabled={syncEngagementMutation.isPending}
                            className="ml-auto"
                          >
                            <RefreshCw className={`h-3 w-3 ${syncEngagementMutation.isPending ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {post.postedAt
                            ? `Posted: ${new Date(post.postedAt).toLocaleDateString()}`
                            : post.scheduledAt
                            ? `Scheduled: ${new Date(post.scheduledAt).toLocaleDateString()}`
                            : `Created: ${new Date(post.createdAt).toLocaleDateString()}`}
                        </span>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedPost(post);
                              postForm.setValue('title', post.title || '');
                              postForm.setValue('content', post.content);
                              postForm.setValue('platforms', post.platforms || []);
                              postForm.setValue('status', post.status);
                              setIsPostDialogOpen(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setPostToDelete(post);
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          {(post.status === 'posted' || post.metadata?.engagement) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setAnalyticsPost(post);
                                setIsPostAnalyticsDialogOpen(true);
                              }}
                            >
                              <BarChart3 className="h-3 w-3 mr-1" />
                              View Analytics
                            </Button>
                          )}
                          {post.status === 'draft' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => {
                                  const fbChannel = facebookStatus?.channels?.[0];
                                  if (fbChannel) {
                                    publishPostMutation.mutate({ 
                                      postId: post.id, 
                                      channelId: fbChannel.id 
                                    });
                                  } else {
                                    toast({ 
                                      title: "Error", 
                                      description: "No Facebook channel found. Please connect Facebook first.",
                                      variant: "destructive"
                                    });
                                  }
                                }}
                                disabled={publishPostMutation.isPending}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Publish
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedPost(post);
                                  setIsScheduleDialogOpen(true);
                                }}
                              >
                                <Clock className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {posts.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Send className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No posts created yet.</p>
                    <p className="text-sm">Create your first social media post to get started.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-4">
              <h3 className="text-lg font-semibold">Social Media Analytics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Total Posts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-blue-600">{posts.length}</p>
                    <p className="text-xs text-gray-500">All social media posts</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Total Engagements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600">
                      {posts.reduce((sum: number, post: any) => {
                        const engagement = post.metadata?.engagement;
                        return sum + (engagement?.likes || 0) + (engagement?.comments || 0) + (engagement?.shares || 0);
                      }, 0)}
                    </p>
                    <p className="text-xs text-gray-500">Likes, comments, and shares</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Published</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-blue-900">
                      {posts.filter((p: any) => p.status === 'posted').length}
                    </p>
                    <p className="text-xs text-gray-500">Successfully posted</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Scheduled</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-orange-600">
                      {posts.filter((p: any) => p.status === 'scheduled').length}
                    </p>
                    <p className="text-xs text-gray-500">Awaiting publication</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Channel Performance</CardTitle>
                  <CardDescription>Analytics for each connected social channel</CardDescription>
                </CardHeader>
                <CardContent>
                  {channels.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Channel</TableHead>
                          <TableHead>Platform</TableHead>
                          <TableHead>Followers</TableHead>
                          <TableHead>Posts</TableHead>
                          <TableHead>Engagement</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {channels.map((channel: any) => {
                          const channelPosts = posts.filter((p: any) => p.platforms?.includes(channel.platform));
                          const totalEngagement = channelPosts.reduce((sum: number, post: any) => {
                            const engagement = post.metadata?.engagement;
                            return sum + (engagement?.likes || 0) + (engagement?.comments || 0) + (engagement?.shares || 0);
                          }, 0);
                          const engagementRate = channelPosts.length > 0 && channel.followerCount > 0 
                            ? ((totalEngagement / channelPosts.length / channel.followerCount) * 100).toFixed(2)
                            : '0.00';
                          
                          return (
                            <TableRow key={channel.id}>
                              <TableCell className="flex items-center gap-2">
                                {getPlatformIcon(channel.platform)}
                                {channel.name}
                              </TableCell>
                              <TableCell className="capitalize">{channel.platform}</TableCell>
                              <TableCell>{channel.followerCount?.toLocaleString() || 'N/A'}</TableCell>
                              <TableCell>{channelPosts.length}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium">{totalEngagement.toLocaleString()}</span>
                                  <span className="text-xs text-gray-500">{engagementRate}%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" onClick={() => setActiveSubTab('posts')}>
                                  <BarChart3 className="h-3 w-3 mr-1" />
                                  View Posts
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No analytics data available.</p>
                      <p className="text-sm">Connect social channels and create posts to see analytics.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Delete Post Confirmation Dialog */}
            <Dialog
              open={isDeleteDialogOpen}
              onOpenChange={(open) => {
                setIsDeleteDialogOpen(open);
                if (!open) {
                  setPostToDelete(null);
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Post</DialogTitle>
                  <DialogDescription>
                    This action cannot be undone. The post will be permanently removed from your social media planner.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {postToDelete && (
                    <div className="rounded-md bg-gray-50 p-3 text-sm">
                      <p className="font-medium mb-1">
                        {postToDelete.title || "Untitled Post"}
                      </p>
                      <p className="text-gray-600 line-clamp-3">
                        {postToDelete.content}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsDeleteDialogOpen(false);
                        setPostToDelete(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (postToDelete) {
                          deletePostMutation.mutate(postToDelete.id);
                        }
                        setIsDeleteDialogOpen(false);
                        setPostToDelete(null);
                      }}
                      disabled={deletePostMutation.isPending}
                    >
                      {deletePostMutation.isPending ? "Deleting..." : "Delete Post"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Per-Post Analytics Dialog */}
            <Dialog
              open={isPostAnalyticsDialogOpen}
              onOpenChange={(open) => {
                setIsPostAnalyticsDialogOpen(open);
                if (!open) {
                  setAnalyticsPost(null);
                }
              }}
            >
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Post Analytics</DialogTitle>
                  <DialogDescription>
                    Detailed performance metrics for this social post.
                  </DialogDescription>
                </DialogHeader>
                {analyticsPost && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium">
                        {analyticsPost.title || "Untitled Post"}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-3">
                        {analyticsPost.content}
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="p-3 rounded-md bg-gray-50">
                        <div className="text-xs text-gray-500">Likes</div>
                        <div className="text-lg font-semibold">
                          {analyticsPost.metadata?.engagement?.likes ?? 0}
                        </div>
                      </div>
                      <div className="p-3 rounded-md bg-gray-50">
                        <div className="text-xs text-gray-500">Comments</div>
                        <div className="text-lg font-semibold">
                          {analyticsPost.metadata?.engagement?.comments ?? 0}
                        </div>
                      </div>
                      <div className="p-3 rounded-md bg-gray-50">
                        <div className="text-xs text-gray-500">Shares</div>
                        <div className="text-lg font-semibold">
                          {analyticsPost.metadata?.engagement?.shares ?? 0}
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 flex justify-between">
                      <span className="capitalize">
                        Status: {analyticsPost.status}
                      </span>
                      <span>
                        {analyticsPost.postedAt
                          ? `Posted: ${new Date(
                              analyticsPost.postedAt
                            ).toLocaleString()}`
                          : analyticsPost.scheduledAt
                          ? `Scheduled: ${new Date(
                              analyticsPost.scheduledAt
                            ).toLocaleString()}`
                          : `Created: ${new Date(
                              analyticsPost.createdAt
                            ).toLocaleString()}`}
                      </span>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* AI Generator Tab */}
            <TabsContent value="ai-generator" className="space-y-4">
              <h3 className="text-lg font-semibold">AI Post Generator</h3>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Generate Social Media Posts with AI
                  </CardTitle>
                  <CardDescription>
                    Use AI to create engaging social media content for your competitions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="aiPrompt">Describe what you want to post about</Label>
                    <Textarea
                      id="aiPrompt"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="e.g., Create a post about our new iPhone giveaway competition, make it exciting and mention the deadline..."
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Button 
                      onClick={handleGenerateAIPost} 
                      disabled={isGenerating || !aiPrompt.trim()}
                      className="primary-gradient"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Post
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">✨ AI Generation Tips</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Be specific about your competition details (prize, deadline, etc.)</li>
                      <li>• Mention the tone you want (exciting, professional, casual)</li>
                      <li>• Include any specific hashtags or mentions you want</li>
                      <li>• Specify platform preferences for better targeting</li>
                    </ul>
                  </div>

                  {generatePostMutation.isError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 text-sm">
                        {generatePostMutation.error?.message?.includes('OpenAI API key') 
                          ? "⚠️ OpenAI API key not configured. Please ask your administrator to add the OpenAI API key to enable AI post generation."
                          : "Failed to generate AI post. Please try again."
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* WhatsApp Credentials Dialog */}
      <Dialog open={isWhatsAppDialogOpen} onOpenChange={setIsWhatsAppDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-green-600">💬</span>
              Connect WhatsApp Business
            </DialogTitle>
            <DialogDescription>
              Enter your WhatsApp Business API credentials to enable messaging functionality.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...whatsappForm}>
            <form onSubmit={whatsappForm.handleSubmit(handleWhatsAppCredentials)} className="space-y-4">
              <FormField
                control={whatsappForm.control}
                name="accessToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access Token *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password"
                        placeholder="Enter your WhatsApp Business API access token"
                        data-testid="input-whatsapp-access-token"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={whatsappForm.control}
                name="phoneNumberId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number ID *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter your WhatsApp Business phone number ID"
                        data-testid="input-whatsapp-phone-id"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={whatsappForm.control}
                name="businessAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Account ID (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter your WhatsApp Business account ID"
                        data-testid="input-whatsapp-business-id"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={whatsappForm.control}
                name="webhookToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Webhook Token (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password"
                        placeholder="Enter your webhook verification token"
                        data-testid="input-whatsapp-webhook-token"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsWhatsAppDialogOpen(false)}
                  className="flex-1"
                  data-testid="button-whatsapp-cancel"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={whatsappCredentialsMutation.isPending}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="button-whatsapp-save"
                >
                  {whatsappCredentialsMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">💬</span>
                      Connect WhatsApp
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Facebook Page Access Token Dialog */}
      <Dialog open={isFacebookPageDialogOpen} onOpenChange={setIsFacebookPageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-blue-600" />
              Connect Facebook Page
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Enter your Facebook Page access token and page details. This will be used to post as the page, not your personal profile.</p>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
                <p className="font-semibold text-blue-900 mb-1">⚠️ Important: Use a Page Access Token</p>
                <ul className="list-disc list-inside text-blue-800 space-y-1">
                  <li>You must use a <strong>Page Access Token</strong>, not a User Access Token</li>
                  <li>Token must have permissions: <code className="bg-blue-100 px-1 rounded">pages_read_engagement</code>, <code className="bg-blue-100 px-1 rounded">pages_manage_posts</code></li>
                  <li>Get your token from <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Facebook Graph API Explorer</a></li>
                  <li>The token will be validated before saving</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>

          <Form {...facebookPageForm}>
            <form onSubmit={facebookPageForm.handleSubmit(handleFacebookPageCredentials)} className="space-y-4">
              <FormField
                control={facebookPageForm.control}
                name="pageAccessToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Page Access Token *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Paste your long-lived Facebook Page access token"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={facebookPageForm.control}
                name="pageId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Page ID *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter your Facebook Page ID"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={facebookPageForm.control}
                name="pageName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Page Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter a display name for this page"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsFacebookPageDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={facebookPageCredentialsMutation.isPending}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {facebookPageCredentialsMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Facebook className="h-4 w-4 mr-2" />
                      Connect Page
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
